import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { NodeProperty } from '@shared/types/node-properties';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CodeMirrorEditor } from '@/components/workflow/node-editor/CodeMirrorEditor';
import {
  CheckCircle2,
  XCircle,
  Wand2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ============================================================================
// EXAMPLE TEMPLATES
// ============================================================================
const EXAMPLE_TEMPLATES = [
  {
    id: 'single-object',
    name: 'Single Object',
    example: `{
  "name": "John Doe",
  "email": "john@example.com",
  "age": 30
}`,
    outputType: 'object' as const,
  },
  {
    id: 'array-of-objects',
    name: 'Array of Objects',
    example: `[
  {
    "name": "Item 1",
    "value": 100
  },
  {
    "name": "Item 2",
    "value": 200
  }
]`,
    outputType: 'array' as const,
  },
  {
    id: 'nested-object',
    name: 'Nested Object',
    example: `{
  "user": {
    "name": "John Doe",
    "contact": {
      "email": "john@example.com",
      "phone": "+1234567890"
    }
  }
}`,
    outputType: 'object' as const,
  },
  {
    id: 'classification',
    name: 'Classification',
    example: `{
  "category": "support",
  "confidence": 0.95,
  "keywords": ["invoice", "payment"]
}`,
    outputType: 'object' as const,
  },
  {
    id: 'action-items',
    name: 'Action Items',
    example: `[
  {
    "task": "Schedule meeting",
    "priority": "high",
    "assignee": "John"
  }
]`,
    outputType: 'array' as const,
  },
];

// ============================================================================
// TYPES
// ============================================================================
type OutputType = 'object' | 'array' | 'either';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  parsedValue?: any;
  inferredType?: 'object' | 'array';
}

interface JsonSchemaEditorRendererProps {
  property: NodeProperty;
  value: string | object;
  onChange: (value: any) => void;
  disabled?: boolean;
  error?: string;
  outputType?: OutputType;
  onOutputTypeChange?: (type: OutputType) => void;
  schemaDescription?: string;
  onSchemaDescriptionChange?: (desc: string) => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Validate JSON string and return detailed result
 */
function validateJson(jsonString: string): ValidationResult {
  if (!jsonString || jsonString.trim() === '') {
    return {
      isValid: false,
      errors: ['JSON example is required'],
    };
  }

  try {
    const parsed = JSON.parse(jsonString);
    const inferredType = Array.isArray(parsed) ? 'array' : 'object';

    return {
      isValid: true,
      errors: [],
      parsedValue: parsed,
      inferredType,
    };
  } catch (e: any) {
    const errorMessage = e.message || 'Invalid JSON';
    let position = '';

    const posMatch = errorMessage.match(/position (\d+)/);
    if (posMatch) {
      position = ` at position ${posMatch[1]}`;
    }

    return {
      isValid: false,
      errors: [`JSON parse error${position}: ${errorMessage}`],
    };
  }
}

// ============================================================================
// MAIN COMPONENT - SIMPLIFIED
// ============================================================================
export const JsonSchemaEditorRenderer: React.FC<JsonSchemaEditorRendererProps> = ({
  property,
  value,
  onChange,
  disabled = false,
  error,
  outputType = 'either',
  onOutputTypeChange,
}) => {
  // Convert value to string for editing
  const valueToString = useCallback((val: string | object | undefined | null): string => {
    if (typeof val === 'string') {
      if (val === '[object Object]' || val.startsWith('[object ')) {
        return '';
      }
      return val;
    }
    if (val !== null && val !== undefined) {
      try {
        return JSON.stringify(val, null, 2);
      } catch {
        return '';
      }
    }
    return '';
  }, []);

  const initialValue = useMemo(() => valueToString(value), [value, valueToString]);

  // Internal editing state (not committed until blur)
  const [editingValue, setEditingValue] = useState(initialValue);
  const [committedValue, setCommittedValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);

  // Sync state when value prop changes (e.g., when node is loaded)
  useEffect(() => {
    if (!isEditing) {
      const newValue = valueToString(value);
      if (newValue !== committedValue && newValue !== '') {
        setEditingValue(newValue);
        setCommittedValue(newValue);
      }
    }
  }, [value, valueToString, isEditing, committedValue]);

  // Validation state
  const [validation, setValidation] = useState<ValidationResult>(() => validateJson(initialValue));

  // Update validation when editing value changes AND auto-save valid JSON
  useEffect(() => {
    const result = validateJson(editingValue);
    setValidation(result);

    // Auto-save valid JSON after a short debounce (so changes persist even without blur)
    if (result.isValid && editingValue !== committedValue) {
      const timer = setTimeout(() => {
        setCommittedValue(editingValue);
        onChange(result.parsedValue);

        // Also update output type
        if (onOutputTypeChange && result.inferredType) {
          onOutputTypeChange(result.inferredType);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timer);
    }
  }, [editingValue, committedValue, onChange, onOutputTypeChange]);

  // Handle value change from CodeMirror (during editing)
  const handleEditChange = useCallback((newValue: string) => {
    setEditingValue(newValue);
    setIsEditing(true);
  }, []);

  // Commit value on blur
  const handleBlur = useCallback(() => {
    setIsEditing(false);

    if (editingValue !== committedValue) {
      setCommittedValue(editingValue);

      const result = validateJson(editingValue);
      if (result.isValid) {
        onChange(result.parsedValue);

        // Auto-update output type based on what they typed
        if (onOutputTypeChange && result.inferredType) {
          onOutputTypeChange(result.inferredType);
        }
      } else {
        onChange(editingValue);
      }
    }
  }, [editingValue, committedValue, onChange, onOutputTypeChange]);

  // Format JSON
  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(editingValue);
      const formatted = JSON.stringify(parsed, null, 2);
      setEditingValue(formatted);
      setCommittedValue(formatted);
      onChange(parsed);
      toast.success('JSON formatted');
    } catch {
      toast.error('Cannot format invalid JSON');
    }
  }, [editingValue, onChange]);

  // Handle template selection
  const handleTemplateSelect = useCallback((templateId: string) => {
    const template = EXAMPLE_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    setEditingValue(template.example);
    setCommittedValue(template.example);

    const result = validateJson(template.example);
    if (result.isValid) {
      onChange(result.parsedValue);
    }

    if (onOutputTypeChange) {
      onOutputTypeChange(template.outputType);
    }

    toast.success(`Applied "${template.name}" template`);
  }, [onChange, onOutputTypeChange]);

  return (
    <div className="space-y-3">
      {/* Template Selector */}
      <Select onValueChange={handleTemplateSelect} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Choose a template..." />
        </SelectTrigger>
        <SelectContent>
          {EXAMPLE_TEMPLATES.map((template) => (
            <SelectItem key={template.id} value={template.id}>
              {template.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Validation Status + Format Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {validation.isValid ? (
            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Valid JSON
              {validation.inferredType && ` (${validation.inferredType})`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">
              <XCircle className="h-3 w-3 mr-1" />
              Invalid JSON
            </Badge>
          )}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFormat}
                disabled={disabled || !validation.isValid}
              >
                <Wand2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Format JSON</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* JSON Editor */}
      <div className={cn(
        'rounded-md border-2 transition-colors',
        validation.isValid ? 'border-green-500/30' : 'border-red-500/30',
        isEditing && 'border-blue-500/50'
      )}>
        <CodeMirrorEditor
          height="200px"
          language="json"
          value={editingValue}
          onChange={handleEditChange}
          onBlur={handleBlur}
          readOnly={disabled}
          showFormatButton={false}
          placeholder="Enter your JSON example structure..."
        />
      </div>

      {/* Validation Errors */}
      {!validation.isValid && validation.errors.length > 0 && (
        <Alert variant="destructive" className="py-2">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {validation.errors.map((err, i) => (
              <span key={i}>{err}</span>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* External error */}
      {error && (
        <Alert variant="destructive" className="py-2">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default JsonSchemaEditorRenderer;
